import type { LocalFinancialResultContract } from "./financial.js";
import type { StudentProfileContract } from "./students.js";
import type { AuthenticatedUserContract } from "./identity.js";

export type FinancialQueueStateContract = "queued" | "syncing" | "synced" | "failed" | "rejected";

export type SyncReasonCodeContract =
  | "IDENTITY_COLLISION"
  | "REFERENCE_COLLISION"
  | "INVALID_ENVELOPE"
  | "INVALID_SIGNATURE"
  | "ACTOR_DISABLED"
  | "INSTALLATION_REVOKED"
  | "OBLIGATION_MISSING"
  | "OBLIGATION_CHANGED"
  | "OVER_ALLOCATED"
  | "RETRYABLE_TRANSPORT"
  | "RETRYABLE_SERVER";

export interface FinancialSyncOutcomeContract {
  readonly outcomeId: string;
  readonly paymentId: string;
  readonly reference: string;
  readonly status: "synced" | "rejected";
  readonly reasonCode?: SyncReasonCodeContract;
  readonly quarantined: boolean;
  readonly serverReceivedAt: string;
}

export interface FinancialQueueItemContract {
  readonly paymentId: string;
  readonly reference: string;
  readonly state: FinancialQueueStateContract;
  readonly createdAt: string;
  readonly attemptCount: number;
  readonly lastAttemptAt?: string;
  readonly reasonCode?: SyncReasonCodeContract;
  readonly outcome?: FinancialSyncOutcomeContract;
}

export interface FinancialSyncSubmissionContract {
  readonly schemaVersion: "phase-03-v1";
  readonly actorUserId: string;
  readonly installationId: string;
  readonly canonicalBytes: string;
  readonly digest: string;
  readonly signature: string;
  readonly record: LocalFinancialResultContract;
}

export interface SignedSyncLeaseContract {
  readonly version: "phase-03-lease-v1";
  readonly installationId: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly schemaVersion: "phase-03-v1";
  readonly cursor: string;
  readonly nonce: string;
  readonly keyId: "phase-03-local-synthetic";
  readonly signature: string;
}

export interface ReplicaSnapshotPageContract {
  readonly version: "phase-03-snapshot-v1";
  readonly snapshotId: string;
  readonly page: number;
  readonly hasMore: boolean;
  readonly students: readonly StudentProfileContract[];
  readonly cursor: string;
  readonly serverTime: string;
  readonly lease: SignedSyncLeaseContract;
  readonly authorizedActor?: AuthenticatedUserContract;
}

export interface ReplicaChangeContract {
  readonly sequence: string;
  readonly entity: "student";
  readonly entityId: string;
  readonly operation: "replace";
  readonly student: StudentProfileContract;
}

export interface ReplicaChangePageContract {
  readonly version: "phase-03-changes-v1";
  readonly changes: readonly ReplicaChangeContract[];
  readonly cursor: string;
  readonly hasMore: boolean;
  readonly serverTime: string;
  readonly lease: SignedSyncLeaseContract;
}

export type CaptureReadinessCodeContract =
  | "READY"
  | "INITIAL_SYNC_REQUIRED"
  | "PERSISTENCE_REQUIRED"
  | "STORAGE_HEADROOM_REQUIRED"
  | "DURABILITY_TEST_REQUIRED"
  | "RECOVERY_REQUIRED"
  | "AUTHORIZATION_REQUIRED"
  | "COUNTER_EXHAUSTED"
  | "LEASE_WARNING_24H"
  | "LEASE_WARNING_72H"
  | "LEASE_EXPIRED"
  | "CLOCK_UNCERTAIN";

export interface CaptureReadinessContract {
  readonly allowed: boolean;
  readonly code: CaptureReadinessCodeContract;
  readonly ageMilliseconds?: number;
}

export const LOCAL_SYNTHETIC_LEASE_PUBLIC_KEY =
  "MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE4hwUTuLzr_0KdYfhWA7g0dewuKtys7v9k_iGvYc6awmXmEVopcCzaBLY5kDE0vb3NtZNEqUksZEuamUmXwVa1Q";

export function syncLeaseSigningValue(lease: Omit<SignedSyncLeaseContract, "signature">): string {
  return [
    lease.version,
    lease.installationId,
    lease.issuedAt,
    lease.expiresAt,
    lease.schemaVersion,
    lease.cursor,
    lease.nonce,
    lease.keyId,
  ].join("\n");
}
